import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';

export const ImpressumPage = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO 
        title="Impressum"
        description="Impressum und rechtliche Angaben von MP Frankendach - Dachdecker Meisterbetrieb in Fürth"
        canonical="https://mp-frankendach.de/impressum"
        noindex={true}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <motion.button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
                whileHover={{ x: -5 }}
              >
                <ArrowLeft size={24} />
                <span className="font-semibold">Zurück</span>
              </motion.button>
              <img
                src="/images/logo.png"
                alt="MP Frankendach Logo"
                className="h-12 w-auto object-contain"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-8 text-gray-900">
              Impressum
            </h1>

            <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
              {/* Angaben gemäß § 5 TMG */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Angaben gemäß § 5 TMG</h2>
                <div className="text-gray-700 space-y-2">
                  <p className="font-semibold text-lg">MP Frankendach GmbH</p>
                  <p>Schwabacher Str. 260</p>
                  <p>90763 Fürth</p>
                  <p>Deutschland</p>
                  <p className="mt-4"><strong>Vertreten durch die Geschäftsführerin:</strong></p>
                  <p>Antesa Strana Peci</p>
                </div>
              </section>

              {/* Registereintrag */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Registereintrag</h2>
                <div className="text-gray-700">
                  <p className="font-semibold mb-2">Eintragung im Handelsregister</p>
                  <p><strong>Registergericht:</strong> Amtsgericht Fürth</p>
                  <p><strong>Registernummer:</strong> HRB 20333</p>
                </div>
              </section>

              {/* Umsatzsteuer-ID */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Umsatzsteuer-ID</h2>
                <p className="text-gray-700">
                  Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
                  <strong>DE357521895</strong>
                </p>
              </section>

              {/* Kontakt */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Kontakt</h2>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Phone className="text-red-600" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">Telefon</p>
                      <a href="tel:+4991198012850" className="text-gray-900 hover:text-red-600">
                        0911 - 980 12 850
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="text-red-600" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">Mobil</p>
                      <a href="tel:+491733666776" className="text-gray-900 hover:text-red-600">
                        +49 173 3666776
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="text-red-600" size={20} />
                    <div>
                      <p className="text-sm text-gray-600">E-Mail</p>
                      <a href="mailto:info@mp-frankendach.de" className="text-gray-900 hover:text-red-600">
                        info@mp-frankendach.de
                      </a>
                    </div>
                  </div>
                </div>
              </section>

              {/* Berufsbezeichnung */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Berufsbezeichnung und berufsrechtliche Regelungen</h2>
                <p className="text-gray-700">
                  <strong>Berufsbezeichnung:</strong> Dachdecker-Meisterbetrieb<br />
                  <strong>Zuständige Kammer:</strong> Handwerkskammer für Mittelfranken<br />
                  <strong>Verliehen in:</strong> Deutschland
                </p>
              </section>

              {/* EU-Streitschlichtung */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">EU-Streitschlichtung</h2>
                <p className="text-gray-700">
                  Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:<br />
                  <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">
                    https://ec.europa.eu/consumers/odr/
                  </a>
                </p>
              </section>

              {/* Verbraucherstreitbeilegung */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Verbraucher­streit­beilegung/Universal­schlichtungs­stelle</h2>
                <p className="text-gray-700">
                  Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
                </p>
              </section>

              {/* Haftungsausschluss */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Haftung für Inhalte</h2>
                <p className="text-gray-700 leading-relaxed">
                  Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
                </p>
              </section>

              {/* Copyright */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Urheberrecht</h2>
                <p className="text-gray-700 leading-relaxed">
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
                </p>
              </section>
            </div>

            <p className="text-sm text-gray-500 mt-8 text-center">
              Stand: Dezember 2024
            </p>
          </motion.div>
        </div>
      </div>
    </>
  );
};